import React, { useCallback, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useApiGet } from '../../../utils/httpClient';
import {
  getDonationDetail,
  patchDonationAckWorkflow,
} from '../../../endpoints/donations';
import { getMyPermissions } from '../../../endpoints/usersTs';
import { hasUserApplicationPermission } from '../../../utils/permissions';
import { formatCurrency } from '../../../utils/formatters';

const ACK_WORKFLOW_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'complete', label: 'Complete' },
  { value: 'legacy_complete', label: 'Legacy complete' },
  { value: 'waived', label: 'Waived' },
  { value: 'not_required', label: 'Not required' },
];

const ackStatusLabel = (value) =>
  ACK_WORKFLOW_OPTIONS.find((o) => o.value === value)?.label ?? value ?? '—';

function LabelValue({ label, children }) {
  return (
    <Grid container spacing={1} sx={{ py: 0.5 }}>
      <Grid item xs={12} sm={4}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={8}>
        <Typography variant="body1">{children}</Typography>
      </Grid>
    </Grid>
  );
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('MMM D, YYYY h:mm A') : value;
}

export default function DonationDetail() {
  const { id } = useParams();
  const [ackSaving, setAckSaving] = useState(false);
  const [ackError, setAckError] = useState(null);

  const { data, isFetching, isError, refetch } = useApiGet(
    ['donation', id],
    getDonationDetail,
    { enabled: !!id, retry: 1 },
    {
      id,
    }
  );

  const { data: permissions } = useApiGet(
    ['my-permissions-donation-detail', id],
    () => getMyPermissions({ international: '1' }),
    { enabled: true }
  );

  const canManage = hasUserApplicationPermission(
    permissions,
    'donations:manage'
  );

  const handleAckStatusChange = useCallback(
    async (event) => {
      const status = event.target.value;
      setAckError(null);
      setAckSaving(true);
      try {
        await patchDonationAckWorkflow({ id, ack_workflow_status: status });
        await refetch();
      } catch (e) {
        setAckError(e?.message || 'Failed to update acknowledgement status');
      } finally {
        setAckSaving(false);
      }
    },
    [id, refetch]
  );

  if (isFetching && !data) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Skeleton variant="text" width={280} height={40} sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" height={200} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rounded" height={200} />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant="rounded" height={120} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (isError || !data) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Button
          component={RouterLink}
          to="/internal/donations"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 2 }}
        >
          Back to donations
        </Button>
        <Alert
          severity="error"
          action={<Button onClick={() => refetch()}>Retry</Button>}
        >
          {isError ? 'Unable to load donation.' : 'Donation not found.'}
        </Alert>
      </Container>
    );
  }

  const donorName = [data.first_name, data.last_name].filter(Boolean).join(' ');
  const paidLabel = data.payment_made ? 'Paid' : 'Unpaid';
  const paidColor = data.payment_made ? 'success' : 'warning';

  return (
    <Container maxWidth="lg" sx={{ py: 3, px: { xs: 2, sm: 3 } }}>
      <Stack spacing={2}>
        <Button
          component={RouterLink}
          to="/internal/donations"
          startIcon={<ArrowBackIcon />}
          sx={{ alignSelf: 'flex-start' }}
        >
          Back to donations
        </Button>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h4" component="h1">
              {donorName || 'Donation'}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              sx={{ mt: 0.5 }}
            >
              <Typography variant="body1" color="text.secondary">
                {formatCurrency(data.amount)}
                {data.created_at &&
                  ` · ${dayjs(data.created_at).format('MMM D, YYYY')}`}
              </Typography>
              <Chip label={paidLabel} color={paidColor} size="small" />
            </Stack>
          </Box>
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Donor
                </Typography>
                <LabelValue label="Name">{donorName || '—'}</LabelValue>
                {data.linked_donor && (
                  <LabelValue label="Linked member">
                    {[data.linked_donor.first_name, data.linked_donor.last_name]
                      .filter(Boolean)
                      .join(' ')}
                    {data.linked_donor.imis_id
                      ? ` (iMIS ${data.linked_donor.imis_id})`
                      : ''}
                  </LabelValue>
                )}
                <LabelValue label="Email">
                  {data.email?.trim() ? data.email : '—'}
                </LabelValue>
                <LabelValue label="Address">
                  <Stack spacing={0}>
                    {data.address1 && <span>{data.address1}</span>}
                    {data.address2 && <span>{data.address2}</span>}
                    {(data.city || data.state || data.postal_code) && (
                      <span>
                        {[data.city, data.state].filter(Boolean).join(', ')}{' '}
                        {data.postal_code}
                      </span>
                    )}
                    {data.country && <span>{data.country}</span>}
                    {!data.address1 && !data.city && !data.country && '—'}
                  </Stack>
                </LabelValue>
                {data.local_chapter && (
                  <LabelValue label="Chapter">{data.local_chapter}</LabelValue>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Payment & integration
                </Typography>
                <LabelValue label="Amount">
                  {formatCurrency(data.amount)}
                </LabelValue>
                <LabelValue label="Payment method">
                  {data.payment_method || '—'}
                </LabelValue>
                {data.check_number && (
                  <LabelValue label="Check number">
                    {data.check_number}
                  </LabelValue>
                )}
                {data.check_payment_id && (
                  <LabelValue label="Check payment ID">
                    {data.check_payment_id}
                  </LabelValue>
                )}
                {data.stripe_payment_id && (
                  <LabelValue label="Stripe payment ID">
                    {data.stripe_payment_id}
                  </LabelValue>
                )}
                <LabelValue label="iMIS ID">{data.imis_id || '—'}</LabelValue>
                <LabelValue label="iMIS acknowledgement ID">
                  {data.imis_acknowledgement_id || '—'}
                </LabelValue>
                <LabelValue label="iMIS sync date">
                  {data.imis_sync_date
                    ? dayjs(data.imis_sync_date).format('MMM D, YYYY')
                    : '—'}
                </LabelValue>
                <LabelValue label="Ack workflow status">
                  {canManage ? (
                    <FormControl size="small" fullWidth disabled={ackSaving}>
                      <InputLabel id="ack-workflow-label">Status</InputLabel>
                      <Select
                        labelId="ack-workflow-label"
                        label="Status"
                        displayEmpty
                        value={data.ack_workflow_status || ''}
                        renderValue={(v) => (v ? ackStatusLabel(v) : '—')}
                        onChange={handleAckStatusChange}
                      >
                        {ACK_WORKFLOW_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {ackSaving && (
                        <CircularProgress size={20} sx={{ mt: 1 }} />
                      )}
                    </FormControl>
                  ) : (
                    ackStatusLabel(data.ack_workflow_status)
                  )}
                </LabelValue>
                {ackError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {ackError}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Typography variant="h6">Donation lines</Typography>
        {(data.donation_lines ?? []).length === 0 ? (
          <Typography color="text.secondary">No donation lines.</Typography>
        ) : (
          (data.donation_lines ?? []).map((line, index) => (
            <Card key={line.id} variant="outlined">
              <CardContent>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Line {index + 1}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatCurrency(line.donation_amount)} ·{' '}
                      {line.project_name}
                      {line.fund_name ? ` · ${line.fund_name}` : ''}
                    </Typography>
                    {line.behalf_of_name && (
                      <Chip
                        size="small"
                        label={
                          line.behalf_of_type_name === 'honor'
                            ? `In honor of ${line.behalf_of_name}`
                            : line.behalf_of_type_name === 'memory'
                              ? `In memory of ${line.behalf_of_name}`
                              : line.behalf_of_name
                        }
                        sx={{ mt: 1 }}
                      />
                    )}
                    {line.local_chapter && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={line.local_chapter}
                        sx={{ mt: 1, ml: 1 }}
                      />
                    )}
                    {line.remain_anonymous && (
                      <Chip
                        size="small"
                        label="Anonymous"
                        sx={{ mt: 1, ml: 1 }}
                      />
                    )}
                  </Box>
                </Stack>
                {(line.notifications ?? []).length > 0 ? (
                  <Table size="small" sx={{ mt: 2 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Method</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Contact</TableCell>
                        <TableCell>Address</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {line.notifications.map((n) => (
                        <TableRow key={n.id}>
                          <TableCell>
                            {n.notification_type === 'mail' ? 'Mail' : 'Email'}
                          </TableCell>
                          <TableCell>{n.full_name || '—'}</TableCell>
                          <TableCell>
                            {n.email?.trim() ? n.email : '—'}
                          </TableCell>
                          <TableCell>
                            {n.notification_type === 'mail' ? (
                              <Stack spacing={0}>
                                {n.address1 && <span>{n.address1}</span>}
                                {(n.city || n.state_id || n.postal_code) && (
                                  <span>
                                    {[n.city, n.state_id]
                                      .filter(Boolean)
                                      .join(', ')}{' '}
                                    {n.postal_code}
                                  </span>
                                )}
                                {!n.address1 && !n.city && '—'}
                              </Stack>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    No notifications for this line.
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))
        )}

        <Typography variant="h6" sx={{ pt: 1 }}>
          Acknowledgements
        </Typography>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Sent
            </Typography>
            {(data.acknowledgements ?? []).length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                No acknowledgements have been sent for this donation yet.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Template</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Sent to</TableCell>
                    <TableCell>Sent at</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.acknowledgements.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.template_type}</TableCell>
                      <TableCell>{row.send_method}</TableCell>
                      <TableCell>{row.sent_to || '—'}</TableCell>
                      <TableCell>{formatDateTime(row.sent_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              In open batch
            </Typography>
            {(data.open_batch_items ?? []).length === 0 ? (
              <Typography color="text.secondary" variant="body2">
                Not in an open batch.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Batch #</TableCell>
                    <TableCell>Template</TableCell>
                    <TableCell>Method</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.open_batch_items.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.batch_number || row.batch_id}</TableCell>
                      <TableCell>{row.template_type}</TableCell>
                      <TableCell>{row.send_method}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
}
